import React, { useEffect } from 'react';
import { Box } from '@atlaskit/primitives';
import { BreadcrumbManager } from '@atlassian/jira-breadcrumbs/src/breadcrumb-manager/index.tsx';
import ProjectBreadcrumb from '@atlassian/jira-breadcrumbs/src/common-breadcrumbs/project/index.tsx';
import Spinner from '@atlassian/jira-common-components-spinner/src/view.tsx';
import withInjectedProps from '@atlassian/jira-common-util-with-injected-props';
import { FlagsDispatcher } from '@atlassian/jira-flags';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { ReduxConnector } from '@atlassian/jira-servicedesk-common/src/ui/components/redux-connector';
import { JsdGettingStartedPanelShouldRender } from '@atlassian/jira-servicedesk-getting-started-panel/src/async';
import { useCategoriesData } from '@atlassian/jira-servicedesk-knowledge-common/src/controllers/store/index.tsx';
import CreateCategoryModal from '@atlassian/jira-servicedesk-knowledge-common/src/ui/create-category/index.tsx';
import flagsMapper from '@atlassian/jira-servicedesk-knowledge-hub/src/flags';
import rootEpic from '@atlassian/jira-servicedesk-knowledge-hub/src/ops';
import {
	KnowledgeHubActionsSubscriber,
	KnowledgeHubContainer as KnowledgeHubContainerDI,
} from '@atlassian/jira-servicedesk-knowledge-hub/src/state/mirror-sweet-state/main.tsx';
import rootReducer from '@atlassian/jira-servicedesk-knowledge-hub/src/state/reducer';
import KnowledgeHubSetter from '@atlassian/jira-servicedesk-knowledge-hub/src/view/knowledge-hub-setter';
import ServiceDeskAppBase from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/app-base/index.tsx';
import { ServiceDeskDocumentTitle } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/document-title/index.tsx';
import { useKbConfluenceContext } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/kb-confluence-context/index.tsx';
import { usePageContext } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/page-context/main.tsx';
import { PageStyle } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/page-style/index.tsx';
import { useRouter } from '@atlassian/react-resource-router';
import type { Props } from './types';

const KnowledgeHubAppBaseView = (props: Props) => {
	const { initialActions, KnowledgeHubContainer, appName, shouldRemoveMargin = false } = props;
	const [{ match, query }] = useRouter();
	const [{ data: pageContextData, error: pageContextError }] = usePageContext();
	const { tenantContext, projectContext, serviceDeskContext } = pageContextData || {};
	const { projectKey, projectName, projectId } = projectContext || {};
	const [{ data: confluenceLink, error: confluenceLinkError }] = useKbConfluenceContext(projectKey);
	const [{ categories, isCategoriesSupported }, { fetchCategories }] = useCategoriesData();
	const { activationId } = tenantContext || {};
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (!getWillShowNav4()) {
			return;
		}
		projectId && activationId && fetchCategories(projectId, createAnalyticsEvent({}), activationId);
	}, [projectId, fetchCategories, createAnalyticsEvent, activationId]);

	if ((!confluenceLink && !confluenceLinkError) || (!pageContextData && !pageContextError)) {
		return <Spinner />;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const stateAppProps: any = {
		app: {
			...tenantContext,
			...projectContext,
			...serviceDeskContext,
		},
	};

	const CustomizedPageStyle = getWillShowNav4() && shouldRemoveMargin ? Box : PageStyle;

	return (
		<>
			<ServiceDeskAppBase appName={appName}>
				<KnowledgeHubContainer scope={projectKey}>
					<KnowledgeHubActionsSubscriber>
						{(_, scopedKnowledgeActions) => {
							const initialState = {
								...scopedKnowledgeActions.getStoreState(),
								...stateAppProps,
							};
							return (
								<ReduxConnector
									storeName="servicedesk-knowledge-hub"
									initialState={initialState}
									rootReducer={rootReducer(scopedKnowledgeActions.updateStore)}
									rootEpic={rootEpic}
									initialActions={initialActions}
								>
									<KnowledgeHubSetter
										{...confluenceLink}
										categories={categories}
										isCategoriesSupported={isCategoriesSupported}
									>
										<ServiceDeskDocumentTitle
											// @ts-expect-error - TS2769 - No overload matches this call.
											projectName={projectName}
											section="knowledge"
										/>
										<CreateCategoryModal />
										<BreadcrumbManager>
											<ProjectBreadcrumb
												baseUrl=""
												// eslint-disable-next-line @typescript-eslint/ban-ts-comment
												// @ts-ignore - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
												projectKey={projectKey}
												// eslint-disable-next-line @typescript-eslint/ban-ts-comment
												// @ts-ignore - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
												projectName={projectName}
											>
												<CustomizedPageStyle>
													{props.children({
														match,
														query,
														pageContext: {
															...stateAppProps.app,
														},
													})}
												</CustomizedPageStyle>
											</ProjectBreadcrumb>
										</BreadcrumbManager>
										<FlagsDispatcher mapper={flagsMapper} />
									</KnowledgeHubSetter>
								</ReduxConnector>
							);
						}}
					</KnowledgeHubActionsSubscriber>
				</KnowledgeHubContainer>
			</ServiceDeskAppBase>
			<JsdGettingStartedPanelShouldRender />
		</>
	);
};

export const KnowledgeHubAppBase = withInjectedProps(KnowledgeHubAppBaseView, {
	initialActions: [],
	KnowledgeHubContainer: KnowledgeHubContainerDI,
});

export default KnowledgeHubAppBase;
